import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import { ImageWithBasePath } from 'Core/components';
import { Search, Settings, User, XCircle } from 'react-feather';
import { all_routes } from '../../router/all_routes';
import { actions as shipmentActions, fetchShipmentsWithFilters } from '../../feature-module/shipment/store/slice';
import { selectShipmentFilters } from '../../feature-module/shipment/store/selectors';
import {
  actions as receivingActions,
  fetchReceivingShipmentsWithFilters,
} from '../../feature-module/mappings/store/slice';
import { selectReceivingShipmentFilters } from '../../feature-module/mappings/store/selectors';
import {
  acknowledgeNotification,
  fetchNotifications,
  formatNotificationTime,
  markAllNotificationsRead,
  markNotificationRead,
  resolveNotificationLinkUrl,
} from '../../service/api/notification-service';
import { startNotificationHub } from '../../service/api/notification-hub';
import { configService } from '../../config/config-service';
import { clearAccessSnapshot } from '../../core/utils/PermissionContext';

const { setSearchText } = shipmentActions;
const { setSearchText: setReceivingSearchText } = receivingActions;

const isShipmentRoute = pathname => {
  const paths = [all_routes.shipmenttile, all_routes.shipmentlist, all_routes.shipments];
  return paths.some(p => p && (pathname === p || pathname.startsWith(`${p}/`)));
};

const isReceivingRoute = pathname => {
  const path = all_routes.mappings;
  return path && (pathname === path || pathname.startsWith(`${path}/`));
};

const Header = () => {
  const route = all_routes;
  const [toggle, SetToggle] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { searchText } = useSelector(selectShipmentFilters);
  const { searchText: receivingSearchText } = useSelector(selectReceivingShipmentFilters);
  const shipmentSearchTimerRef = useRef(null);
  const receivingSearchTimerRef = useRef(null);

  const isShipmentPage = isShipmentRoute(location.pathname);
  const isReceivingPage = isReceivingRoute(location.pathname);
  const isLineItemSearchPage = isShipmentPage || isReceivingPage;

  const notificationsStartedRef = useRef(false);

  /** Delay so Change Request / other page APIs go first (avoids competing on first paint). */
  const NOTIFICATION_DEFER_MS = 4000;

  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    setNotificationsError(null);
    try {
      const data = await fetchNotifications({ page: 1, pageSize: 10, filter: 'all' });
      setNotifications(Array.isArray(data.items) ? data.items : []);
      // Badge must use API total — dropdown list is capped at pageSize 10.
      setUnreadCount(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
    } catch (err) {
      console.error('Failed to load notifications', err);
      setNotificationsError(err.message || 'Failed to load notifications');
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const attachRealtime = useCallback(() => {
    const onReceive = dto => {
      if (!dto || !dto.notificationId) return;
      let added = false;
      setNotifications(prev => {
        if (prev.some(n => n.notificationId === dto.notificationId && n.recipientId === dto.recipientId)) {
          return prev;
        }
        added = true;
        return [dto, ...prev].slice(0, 10);
      });
      if (added && !dto.isRead) {
        setUnreadCount(c => c + 1);
      }
      setNotificationsError(null);
    };

    return startNotificationHub(onReceive).catch(err => {
      console.warn('SignalR unavailable; bell will rely on polling.', err);
      return { unsubscribe: () => {} };
    });
  }, []);

  // Defer inbox fetch + SignalR until after page APIs have had time to start.
  useEffect(() => {
    let intervalId;
    let unsubscribe = () => {};
    let cancelled = false;

    const timerId = setTimeout(() => {
      if (cancelled || notificationsStartedRef.current) return;
      notificationsStartedRef.current = true;
      loadNotifications();
      attachRealtime().then(result => {
        if (cancelled) {
          result?.unsubscribe?.();
          return;
        }
        unsubscribe = result?.unsubscribe || (() => {});
      });
      intervalId = setInterval(loadNotifications, 60000);
    }, NOTIFICATION_DEFER_MS);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      if (intervalId) clearInterval(intervalId);
      unsubscribe();
    };
  }, [loadNotifications, attachRealtime]);

  const handleBellClick = () => {
    loadNotifications();
    if (!notificationsStartedRef.current) {
      notificationsStartedRef.current = true;
      attachRealtime();
    }
  };

  const handleNotificationClick = async item => {
    try {
      if (!item.isRead) {
        await markNotificationRead(item.notificationId);
        setNotifications(prev =>
          prev.map(n =>
            n.notificationId === item.notificationId
              ? { ...n, isRead: true, readDate: new Date().toISOString() }
              : n,
          ),
        );
        setUnreadCount(c => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Mark read failed', err);
    }

    const linkUrl = resolveNotificationLinkUrl(item);
    if (linkUrl) {
      navigate(linkUrl);
    }
  };

  const handleAcknowledge = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const wasUnread = !item.isRead;
      await acknowledgeNotification(item.notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.notificationId === item.notificationId
            ? {
                ...n,
                isRead: true,
                isAcknowledged: true,
                acknowledgedDate: new Date().toISOString(),
              }
            : n,
        ),
      );
      if (wasUnread) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Acknowledge failed', err);
    }
  };

  const handleClearAll = async e => {
    e.preventDefault();
    try {
      await markAllNotificationsRead();
      await loadNotifications();
    } catch (err) {
      console.error('Mark all read failed', err);
    }
  };

  const isElementVisible = element => {
    return element.offsetWidth > 0 || element.offsetHeight > 0;
  };

  useEffect(() => {
    const handleMouseover = e => {
      e.stopPropagation();

      const body = document.body;
      const toggleBtn = document.getElementById('toggle_btn');

      if (body.classList.contains('mini-sidebar') && isElementVisible(toggleBtn)) {
        e.preventDefault();
      }
    };

    document.addEventListener('mouseover', handleMouseover);

    return () => {
      document.removeEventListener('mouseover', handleMouseover);
    };
  }, []);
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement,
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(
    () => () => {
      if (shipmentSearchTimerRef.current) {
        clearTimeout(shipmentSearchTimerRef.current);
      }
      if (receivingSearchTimerRef.current) {
        clearTimeout(receivingSearchTimerRef.current);
      }
    },
    [],
  );

  const handleShipmentHeaderSearchChange = e => {
    const value = e.target.value;
    dispatch(setSearchText(value));
    if (shipmentSearchTimerRef.current) {
      clearTimeout(shipmentSearchTimerRef.current);
    }
    shipmentSearchTimerRef.current = setTimeout(() => {
      dispatch(fetchShipmentsWithFilters());
    }, 400);
  };

  const handleReceivingHeaderSearchChange = e => {
    const value = e.target.value;
    dispatch(setReceivingSearchText(value));
    if (receivingSearchTimerRef.current) {
      clearTimeout(receivingSearchTimerRef.current);
    }
    receivingSearchTimerRef.current = setTimeout(() => {
      dispatch(fetchReceivingShipmentsWithFilters());
    }, 400);
  };

  const handlesidebar = () => {
    document.body.classList.toggle('mini-sidebar');
    SetToggle(current => !current);
  };
  const expandMenu = () => {
    document.body.classList.remove('expand-menu');
  };
  const expandMenuOpen = () => {
    document.body.classList.add('expand-menu');
  };
  const sidebarOverlay = () => {
    document?.querySelector('.main-wrapper')?.classList?.toggle('slide-nav');
    document?.querySelector('.sidebar-overlay')?.classList?.toggle('opened');
    document?.querySelector('html')?.classList?.toggle('menu-opened');
  };

  let pathname = window.location.pathname;
  let userData = localStorage.getItem('LoginInfo');
  let userName = localStorage.getItem('UserName');

  const exclusionArray = [
    '/reactjs/template/dream-pos/index-three',
    '/reactjs/template/dream-pos/index-one',
  ];
  if (exclusionArray.indexOf(window.location.pathname) >= 0) {
    return '';
  }

  const toggleFullscreen = elem => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    // Clear all authentication related localStorage items
    localStorage.removeItem('LoginInfo');
    localStorage.removeItem('UserName');
    localStorage.removeItem('UserId');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('Token');
    clearAccessSnapshot();
    // Redirect to signin page
    navigate('/signin');
  };

  return (
    <>
      <div className="header">
        {/* Logo */}
        <div
          className={`header-left ${toggle ? '' : 'active'}`}
          onMouseLeave={expandMenu}
          onMouseOver={expandMenuOpen}
        >
          <Link to="/dashboard" className="logo logo-normal">
            <ImageWithBasePath src="assets/img/logo.png" alt="img" />
          </Link>
          <Link to="/dashboard" className="logo logo-white">
            <ImageWithBasePath src="assets/img/logo-white.png" alt="img" />
          </Link>
          <Link to="/dashboard" className="logo-small">
            <ImageWithBasePath src="assets/img/logo-small.png" alt="img" />
          </Link>
          <Link
            id="toggle_btn"
            to="#"
            style={{
              display:
                pathname.includes('tasks') || pathname.includes('pos')
                  ? 'none'
                  : pathname.includes('compose')
                    ? 'none'
                    : '',
            }}
            onClick={handlesidebar}
          >
            <FeatherIcon icon="chevrons-left" className="feather-16" />
          </Link>
        </div>
        {/* /Logo */}
        <Link id="mobile_btn" className="mobile_btn" to="#" onClick={sidebarOverlay}>
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        {/* Header Menu */}
        <ul className="nav user-menu">
          {/* Search */}
          <li className="nav-item nav-searchinputs">
            <div className="top-nav-search">
              <Link to="#" className="responsive-search">
                <Search />
              </Link>
              <form action="#" className={isLineItemSearchPage ? '' : 'dropdown'}>
                <div
                  className={isLineItemSearchPage ? 'searchinputs' : 'searchinputs dropdown-toggle'}
                  id="dropdownMenuClickable"
                  {...(!isLineItemSearchPage
                    ? { 'data-bs-toggle': 'dropdown', 'data-bs-auto-close': 'false' }
                    : {})}
                >
                  {isShipmentPage ? (
                    <input
                      type="text"
                      placeholder="Search part # or PO #"
                      value={searchText ?? ''}
                      onChange={handleShipmentHeaderSearchChange}
                      aria-label="Search shipments by part number or PO number"
                    />
                  ) : isReceivingPage ? (
                    <input
                      type="text"
                      placeholder="Search part # or PO #"
                      value={receivingSearchText ?? ''}
                      onChange={handleReceivingHeaderSearchChange}
                      aria-label="Search receiving shipments by part number or PO number"
                    />
                  ) : (
                    <input type="text" placeholder="Search" />
                  )}
                  <div className="search-addon">
                    <span>
                      <XCircle className="feather-14" />
                    </span>
                  </div>
                </div>
                {!isLineItemSearchPage && (
                  <div
                    className="dropdown-menu search-dropdown"
                    aria-labelledby="dropdownMenuClickable"
                  >
                    <div className="search-info">
                      <h6>
                        <span>
                          <i data-feather="search" className="feather-16" />
                        </span>
                        Recent Searches
                      </h6>
                      <ul className="search-tags">
                        <li>
                          <Link to="#">Products</Link>
                        </li>
                        <li>
                          <Link to="#">Sales</Link>
                        </li>
                        <li>
                          <Link to="#">Applications</Link>
                        </li>
                      </ul>
                    </div>
                    <div className="search-info">
                      <h6>
                        <span>
                          <i data-feather="help-circle" className="feather-16" />
                        </span>
                        Help
                      </h6>
                      <p>How to Change Product Volume from 0 to 200 on Inventory management</p>
                      <p>Change Product Name</p>
                    </div>
                    <div className="search-info">
                      <h6>
                        <span>
                          <i data-feather="user" className="feather-16" />
                        </span>
                        Customers
                      </h6>
                      <ul className="customers">
                        <li>
                          <Link to="#">
                            Aron Varu
                            <ImageWithBasePath
                              src="assets/img/profiles/avator1.jpg"
                              alt
                              className="img-fluid"
                            />
                          </Link>
                        </li>
                        <li>
                          <Link to="#">
                            Jonita
                            <ImageWithBasePath
                              src="assets/img/profiles/avatar-01.jpg"
                              alt
                              className="img-fluid"
                            />
                          </Link>
                        </li>
                        <li>
                          <Link to="#">
                            Aaron
                            <ImageWithBasePath
                              src="assets/img/profiles/avatar-10.jpg"
                              alt
                              className="img-fluid"
                            />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </li>
          {/* /Search */}

          {/* Select Store */}
          {/* <li className="nav-item dropdown has-arrow main-drop select-store-dropdown">
            <Link
              to="#"
              className="dropdown-toggle nav-link select-store"
              data-bs-toggle="dropdown"
            >
              <span className="user-info">
                <span className="user-letter">
                  <ImageWithBasePath
                    src="assets/img/store/store-01.png"
                    alt="Store Logo"
                    className="img-fluid"
                  />
                </span>
                <span className="user-detail">
                  <span className="user-name">Select Store</span>
                </span>
              </span>
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/store/store-01.png"
                  alt="Store Logo"
                  className="img-fluid"
                />{" "}
                Grocery Alpha
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/store/store-02.png"
                  alt="Store Logo"
                  className="img-fluid"
                />{" "}
                Grocery Apex
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/store/store-03.png"
                  alt="Store Logo"
                  className="img-fluid"
                />{" "}
                Grocery Bevy
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/store/store-04.png"
                  alt="Store Logo"
                  className="img-fluid"
                />{" "}
                Grocery Eden
              </Link>
            </div>
          </li> */}
          {/* /Select Store */}

          {/* Flag */}
          {/* <li className="nav-item dropdown has-arrow flag-nav nav-item-box">
            <Link
              className="nav-link dropdown-toggle"
              data-bs-toggle="dropdown"
              to="#"
              role="button"
            >
             
              <ImageWithBasePath
                src="assets/img/flags/us.png"
                alt="img"
                height={16}
              />
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to="#" className="dropdown-item active">
                <ImageWithBasePath
                  src="assets/img/flags/us.png"
                  alt="img"
                  height={16}
                />
                English
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/flags/fr.png"
                  alt="img"
                  height={16}
                />{" "}
                French
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/flags/es.png"
                  alt="img"
                  height={16}
                />{" "}
                Spanish
              </Link>
              <Link to="#" className="dropdown-item">
                <ImageWithBasePath
                  src="assets/img/flags/de.png"
                  alt="img"
                  height={16}
                />{" "}
                German
              </Link>
            </div>
          </li> */}
          {/* /Flag */}

          {/* Start Fullscreen */}
          <li className="nav-item nav-item-box">
            <Link
              to="#"
              id="btnFullscreen"
              onClick={() => toggleFullscreen()}
              className={isFullscreen ? 'Exit Fullscreen' : 'Go Fullscreen'}
            >
              {/* <i data-feather="maximize" /> */}
              <FeatherIcon icon="maximize" />
            </Link>
          </li>
          {/* End Fullscreen */}

          {/* Start Email */}
          {/* <li className="nav-item nav-item-box">
            <Link to="/email">               
              <FeatherIcon icon="mail" />
              <span className="badge rounded-pill">1</span>
            </Link>
          </li> */}
          {/* End Email */}

          {/* Start Notifications */}
          <li className="nav-item dropdown nav-item-box">
            <Link
              to="#"
              className="dropdown-toggle nav-link"
              data-bs-toggle="dropdown"
              onClick={handleBellClick}
            >
              <FeatherIcon icon="bell" />
              {unreadCount > 0 && (
                <span className="badge rounded-pill">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </Link>
            <div className="dropdown-menu notifications">
              <div className="topnav-dropdown-header">
                <span className="notification-title">Notifications</span>
                <Link to="#" className="clear-noti" onClick={handleClearAll}>
                  {' '}
                  Mark all as read{' '}
                </Link>
              </div>
              <div className="noti-content">
                <ul className="notification-list">
                  {notificationsLoading && (
                    <li className="notification-message">
                      <div className="media-body flex-grow-1 p-3">
                        <p className="noti-details">Loading…</p>
                      </div>
                    </li>
                  )}
                  {!notificationsLoading && notificationsError && (
                    <li className="notification-message">
                      <div className="media-body flex-grow-1 p-3">
                        <p className="noti-details text-danger">{notificationsError}</p>
                        <p className="noti-time">
                          <span className="notification-time">
                            Is Notification.API running on {configService.notificationApiUrl}?
                          </span>
                        </p>
                      </div>
                    </li>
                  )}
                  {!notificationsLoading && !notificationsError && notifications.length === 0 && (
                    <li className="notification-message">
                      <div className="media-body flex-grow-1 p-3">
                        <p className="noti-details">No notifications</p>
                      </div>
                    </li>
                  )}
                  {!notificationsLoading &&
                    !notificationsError &&
                    notifications.map(item => (
                      <li
                        key={item.recipientId || item.notificationId}
                        className={`notification-message${item.isRead ? '' : ' active'}`}
                      >
                        <Link
                          to="#"
                          onClick={e => {
                            e.preventDefault();
                            handleNotificationClick(item);
                          }}
                        >
                          <div className="media d-flex">
                            <span className="avatar flex-shrink-0">
                              <span
                                className="rounded-circle d-inline-flex align-items-center justify-content-center bg-primary text-white"
                                style={{ width: 36, height: 36, fontSize: 12 }}
                              >
                                {(item.category || 'N').slice(0, 3)}
                              </span>
                            </span>
                            <div className="media-body flex-grow-1">
                              <p className="noti-details">
                                <span className="noti-title">{item.title}</span>
                              </p>
                              <p className="noti-details" style={{ fontWeight: 400 }}>
                                {item.message}
                              </p>
                              <p className="noti-time">
                                <span className="notification-time">
                                  {formatNotificationTime(item.createdDate)}
                                  {item.requiresAck && !item.isAcknowledged ? ' · Needs ack' : ''}
                                  {item.isAcknowledged ? ' · Acknowledged' : ''}
                                </span>
                              </p>
                              {item.requiresAck && !item.isAcknowledged && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary mt-1"
                                  onClick={e => handleAcknowledge(e, item)}
                                >
                                  Acknowledge
                                </button>
                              )}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="topnav-dropdown-footer">
                <Link
                  to={all_routes.notifications}
                  onClick={() => {
                    // Close Bootstrap dropdown by blurring active element if needed.
                  }}
                >
                  View All Notifications
                </Link>
              </div>
            </div>
          </li>
          {/* End Notifications */}

          {/* Start Settings */}

          {/* <li className="nav-item nav-item-box">
            <Link to="/general-settings">             
              <FeatherIcon icon="settings" />
            </Link>
          </li> */}

          {/* End Settings */}

          {/* Start User */}

          <li className="nav-item dropdown has-arrow main-drop">
            <Link to="#" className="dropdown-toggle nav-link userset" data-bs-toggle="dropdown">
              <span className="user-info">
                <span className="user-letter">
                  <ImageWithBasePath
                    src="assets/img/profiles/avator1.jpg"
                    alt="img"
                    className="img-fluid"
                  />
                </span>
                <span className="user-detail">
                  <span className="user-name">{userName}</span>
                  {/* <span className="user-role">Super Admin</span> */}
                </span>
              </span>
            </Link>
            <div className="dropdown-menu menu-drop-user">
              <div className="profilename">
                <div className="profileset">
                  <span className="user-img">
                    <ImageWithBasePath src="assets/img/profiles/avator1.jpg" alt="img" />
                    <span className="status online" />
                  </span>
                  <div className="profilesets">
                    <h6>{userName}</h6>
                    {/* <h5>Super Admin</h5> */}
                  </div>
                </div>
                {/* <hr className="m-0" />
                <Link className="dropdown-item" to={route.profile}>
                  <User className="me-2" /> My Profile
                </Link>
                <Link className="dropdown-item" to={route.generalsettings}>
                  <Settings className="me-2" />
                  Settings
                </Link> */}
                <hr className="m-0" />
                <a className="dropdown-item logout pb-0" href="#" onClick={handleLogout}>
                  <ImageWithBasePath
                    src="assets/img/icons/log-out.svg"
                    alt="img"
                    className="me-2"
                  />
                  Logout
                </a>
              </div>
            </div>
          </li>
          {/* End User */}
        </ul>
        {/* /Header Menu */}

        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            {/* <Link className="dropdown-item" to="profile">
              My Profile
            </Link>
            <Link className="dropdown-item" to="generalsettings">
              Settings
            </Link> */}
            <a className="dropdown-item" href="#" onClick={handleLogout}>
              Logout
            </a>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>
    </>
  );
};

export default Header;
